// Arma el texto de una notificación en el idioma actual. La API guarda solo
// `tipo` + `datos`; así el aviso se lee en el idioma que el usuario tenga
// elegido en ese momento. Lo usan la campana, los carteles y los avisos del
// sistema operativo.

const LOCALES = { es: "es-CL", en: "en-US", pt: "pt-BR", fr: "fr-FR", it: "it-IT", de: "de-DE" };

/** Nombre del alojamiento: viene en los 6 idiomas o como texto. */
export function nombreAlojamiento(valor, idioma) {
    if (!valor) return "";
    if (typeof valor === "string") return valor;
    return valor[idioma] || valor.es || Object.values(valor).find(Boolean) || "";
}

function fechas(d, idioma) {
    if (!d.fecha_inicio) return "";
    const loc = LOCALES[idioma] || "es";
    const f = (s) => {
        const x = new Date(`${s}T12:00:00`);
        return isNaN(x) ? s : new Intl.DateTimeFormat(loc, { day: "numeric", month: "short", year: "numeric" }).format(x);
    };
    return d.fecha_termino ? `${f(d.fecha_inicio)} – ${f(d.fecha_termino)}` : f(d.fecha_inicio);
}

const ICONO = {
    mensaje: "msg",
    reserva_nueva: "cal",
    reserva_nueva_admin: "cal",
    reserva_aceptada: "ok",
    reserva_auto_aceptada: "ok",
    reserva_rechazada: "x",
    reserva_cancelada: "x",
    reserva_pagada: "pay",
    reserva_pagada_huesped: "pay",
    reserva_pagada_admin: "pay",
    alojamiento_nuevo: "home",
    alojamiento_duplicado: "home",
    alojamiento_aprobado: "ok",
    alojamientos_aprobados: "ok",
    alojamiento_eliminado: "x",
    alojamientos_eliminados: "x",
    alojamiento_eliminado_propio: "ok",
    alojamiento_creado: "home",
    alojamiento_editado: "ok",
    alojamientos_editados: "ok",
    // Tableros personales
    tablero_compartido: "board",
    tablero_permiso: "board",
    tablero_sin_acceso: "x",
    tablero_asignada: "board",
    tablero_vista: "board",
    tablero_traspaso: "board",
    tablero_eliminado: "x",
    tablero_salio: "x",
    tablero_seguimiento: "cal",
};

/** Ícono según el tipo (claves de ICONOS en NotificacionesCampana). */
export function familia(n) {
    if (n.tipo === "admin_accion") return ICONO_ADMIN[(n.datos || {}).accion] || "ok";
    return ICONO[n.tipo] || "msg";
}

// Acciones de un admin (tipo admin_accion; el aviso va solo a él).
const ICONO_ADMIN = {
    alojamiento_creado: "home",
    alojamiento_duplicado: "home",
    alojamiento_editado: "ok",
    alojamiento_eliminado: "x",
    alojamientos_eliminados: "x",
    alojamientos_editados: "ok",
    reserva_creada: "cal",
    reserva_pagada: "pay",
    perfil_editado: "ok",
    datos_bancarios: "pay",
    contrasena: "ok",
};

/**
 * @returns {{ titulo: string, cuerpo: string, meta: string }}
 */
export function textoNotificacion(n, i18n) {
    const idioma = i18n.locale;
    const d = n.datos || {};
    const t = (k, p) => i18n.t(`notificaciones.tipos.${k}`, p);
    const params = {
        nombre: d.nombre || i18n.t("notificaciones.alguien"),
        alojamiento: nombreAlojamiento(d.alojamiento, idioma) || (d.alojamiento_id ? `#${d.alojamiento_id}` : ""),
        anfitrion: d.anfitrion || "",
        fechas: fechas(d, idioma),
        // `datos.cantidad` (aprobados / eliminados / editados en bloque) manda sobre
        // `cantidad` de la fila, que es cuántas veces se agrupó el aviso (mensajes).
        n: d.cantidad || n.cantidad || 1,
        id: d.alojamiento_id || "",
        original: d.original_id || "",
        reserva: d.reserva_id || "",
        // Tableros personales
        tablero: d.tablero || "",
        tarjeta: d.tarjeta || "",
        vista: d.vista || "",
        por: d.por || i18n.t("notificaciones.alguien"),
        rol: d.rol ? i18n.t(`notificaciones.tipos.tablero_rol_${d.rol}`) : "",
        fecha: d.fecha ? new Intl.DateTimeFormat(LOCALES[idioma] || "es", { day: "numeric", month: "short" }).format(new Date(`${d.fecha}T12:00:00`)) : "",
    };

    const tipo = n.tipo === "tablero_seguimiento" && d.hoy ? "tablero_seguimiento_hoy" : n.tipo;
    if (tipo === "admin_accion") {
        // Acción de un admin: "propia" = sobre su propia cuenta (perfil, banco, contraseña).
        const k = `admin.${d.accion}${d.propia === false ? "_otro" : ""}`;
        const base = i18n.te(`notificaciones.tipos.${k}_titulo`) ? k : `admin.${d.accion}`;
        if (i18n.te(`notificaciones.tipos.${base}_titulo`)) {
            return { titulo: t(`${base}_titulo`, { ...params, usuario: d.usuario || "" }), cuerpo: t(`${base}_cuerpo`, { ...params, usuario: d.usuario || "" }), meta: "" };
        }
        return { titulo: i18n.t("notificaciones.generica"), cuerpo: "", meta: "" };
    }
    if (tipo === "mensaje") {
        return {
            titulo: params.n > 1 ? t("mensaje_varios_titulo", params) : t("mensaje_titulo", params),
            cuerpo: d.extracto || "",
            meta: params.alojamiento,
        };
    }
    if (i18n.te(`notificaciones.tipos.${tipo}_titulo`)) {
        return {
            titulo: t(`${tipo}_titulo`, params),
            cuerpo: t(`${tipo}_cuerpo`, params),
            meta: "",
        };
    }
    return { titulo: i18n.t("notificaciones.generica"), cuerpo: "", meta: "" };
}

/** "hace 5 min", "ayer"… */
export function haceCuanto(fecha, idioma) {
    const d = new Date(fecha);
    if (isNaN(d)) return "";
    const seg = Math.round((d.getTime() - Date.now()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(LOCALES[idioma] || "es", { numeric: "auto", style: "short" });
    const abs = Math.abs(seg);
    if (abs < 45) return rtf.format(0, "second");
    if (abs < 3600) return rtf.format(Math.round(seg / 60), "minute");
    if (abs < 86400) return rtf.format(Math.round(seg / 3600), "hour");
    if (abs < 86400 * 7) return rtf.format(Math.round(seg / 86400), "day");
    return new Intl.DateTimeFormat(LOCALES[idioma] || "es", { day: "numeric", month: "short" }).format(d);
}
